import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiStar, FiGlobe, FiEdit3, FiTrash2, FiEye, FiCopy, FiBarChart2 } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { useClient } from '../../contexts/ClientContext';
import { dashboardAPI } from '../../services/realApi';

interface Dashboard {
  id: string | number;
  name: string;
  description?: string;
  context?: string;
  widget_count?: number;
  is_default?: boolean;
  is_published?: boolean;
  created_by?: string | number;
  created_by_name?: string;
}

const DashboardMarketplace: React.FC = () => {
  const { user } = useAuth();
  const { selectedClientId } = useClient();
  const navigate = useNavigate();
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showMyDashboards, setShowMyDashboards] = useState(false);

  const loadDashboards = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await dashboardAPI.getDashboards(
        selectedClientId,
        showMyDashboards
      );
      if (result.success) {
        setDashboards(result.data || []);
      } else {
        setError(result.error || 'Failed to load dashboards');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [selectedClientId, showMyDashboards]);

  useEffect(() => {
    loadDashboards();
  }, [loadDashboards]);

  const handleCreateNew = (): void => {
    navigate('/hub/dashboards/designer');
  };

  const handleEdit = (dashboardId: string | number): void => {
    navigate(`/hub/dashboards/designer/${dashboardId}`);
  };

  const handleView = (dashboardId: string | number): void => {
    navigate(`/hub/dashboards/${dashboardId}`);
  };

  const handleDuplicate = (dashboardId: string | number): void => {
    navigate(`/hub/dashboards/designer?from=${dashboardId}`);
  };

  const handleDelete = async (dashboardId: string | number, e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (!globalThis.confirm('Are you sure you want to delete this dashboard?')) {
      return;
    }

    try {
      const result = await dashboardAPI.deleteDashboard(String(dashboardId));
      if (result.success) {
        loadDashboards();
      } else {
        alert(result.error || 'Failed to delete dashboard');
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : String(err));
    }
  };

  const handlePublish = async (dashboardId: string | number, e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    try {
      const result = await dashboardAPI.publishDashboard(String(dashboardId));
      if (result.success) {
        loadDashboards();
      } else {
        alert(result.error || 'Failed to publish dashboard');
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : String(err));
    }
  };

  const handleSetDefault = async (dashboardId: string | number, dashboardContext: string | undefined, e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    try {
      const result = await dashboardAPI.setDefaultPreference('custom', String(dashboardId), dashboardContext || 'hub', selectedClientId);
      if (result.success) {
        loadDashboards();
      } else {
        alert(result.error || 'Failed to set default dashboard');
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : String(err));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Dashboard Marketplace
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Browse and use dashboards published by your organization
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowMyDashboards(!showMyDashboards)}
              className={`px-4 py-2 border rounded-md transition-colors ${
                showMyDashboards
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {showMyDashboards ? 'Show All' : 'My Dashboards'}
            </button>
            <button
              onClick={handleCreateNew}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              <FiPlus className="w-4 h-4" />
              <span>Create Dashboard</span>
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {dashboards.length === 0 ? (
        <div className="text-center py-12">
          <FiBarChart2 className="w-10 h-10 mb-4 mx-auto text-gray-400" />
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {showMyDashboards
              ? "You haven't created any dashboards yet."
              : 'No dashboards available in your organization.'}
          </p>
          <button
            onClick={handleCreateNew}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Create Your First Dashboard
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {dashboards.map((dashboard) => (
            <div
              key={dashboard.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer border border-gray-200 dark:border-gray-700"
              onClick={() => handleView(dashboard.id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleView(dashboard.id); }} role="button" tabIndex={0}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                      {dashboard.name}
                    </h3>
                    {dashboard.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                        {dashboard.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    {dashboard.is_default && (
                      <FiStar className="w-5 h-5 text-yellow-500 fill-yellow-500" title="Default Dashboard" />
                    )}
                    {dashboard.is_published && (
                      <FiGlobe className="w-5 h-5 text-green-500" title="Published" />
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-4">
                  {dashboard.context && (
                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">
                      {dashboard.context}
                    </span>
                  )}
                  <span>{dashboard.widget_count || 0} widgets</span>
                  {dashboard.created_by_name && (
                    <span>• by {dashboard.created_by_name}</span>
                  )}
                </div>

                <div className="flex items-center gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleView(dashboard.id);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                  >
                    <FiEye className="w-4 h-4" />
                    <span>View</span>
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDuplicate(dashboard.id);
                    }}
                    className="px-3 py-2 border border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    title="Duplicate"
                  >
                    <FiCopy className="w-4 h-4" />
                  </button>

                  {dashboard.created_by === user?.id && (
                    <>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEdit(dashboard.id); }}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        title="Edit"
                      >
                        <FiEdit3 className="w-4 h-4" />
                      </button>
                      {!dashboard.is_published && (
                        <button
                          onClick={(e) => handlePublish(dashboard.id, e)}
                          className="px-3 py-2 border border-green-300 dark:border-green-600 text-green-700 dark:text-green-300 rounded-md hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                          title="Publish"
                        >
                          <FiGlobe className="w-4 h-4" />
                        </button>
                      )}
                      {dashboard.is_published && !dashboard.is_default && (
                        <button
                          onClick={(e) => handleSetDefault(dashboard.id, dashboard.context, e)}
                          className="px-3 py-2 border border-yellow-300 dark:border-yellow-600 text-yellow-700 dark:text-yellow-300 rounded-md hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-colors"
                          title="Set as Default"
                        >
                          <FiStar className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => handleDelete(dashboard.id, e)}
                        className="px-3 py-2 border border-red-300 dark:border-red-600 text-red-700 dark:text-red-300 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        title="Delete"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DashboardMarketplace;
